<script setup lang="ts">
/**
 * Approve a deposit (recharge) request — operator picks the treasury
 * bank account that received the inbound transfer and captures the
 * bank's reference (UTR / SWIFT MT103 / matched statement line) so
 * the credit can be reconciled.
 *
 * The picker is sourced from the same `useTreasuryBankAccountsQuery`
 * the withdraw module already publishes — keeps a single source of
 * truth for the treasury picker across both queues.
 *
 * The row marked `isDefault: true` (or first when none) is auto-
 * selected when the dialog opens. The selected account is shown in the
 * confirm copy so the operator sees the consequence before clicking.
 */
import { computed, ref, watch } from 'vue'
import Dialog from '@uiKit/elements/Dialog.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Select from '@uiKit/elements/Select.vue'
import Input from '@uiKit/elements/Input.vue'
import Message from '@uiKit/elements/Message.vue'
import FormField from '@uiKit/components/FormField.vue'
import type { TreasuryBankAccount } from '../../treasuryAccounts'

interface Props {
  visible: boolean
  currencyCode: string
  accounts: TreasuryBankAccount[]
  accountsLoading?: boolean
  loading?: boolean
  apiError?: string | null
  // The UTR the investor entered when creating this deposit. The admin's
  // captured bank reference must match it — approval is blocked otherwise.
  investorReference?: string | null
}

const MIN_BANK_REFERENCE_LENGTH = 3

const props = withDefaults(defineProps<Props>(), {
  accountsLoading: false,
  loading: false,
  apiError: null,
  investorReference: null,
})

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'confirm', value: { treasuryBankAccountId: string; approvedBankReference: string }): void
}>()

const accounts = computed(() => props.accounts ?? [])
const accountsLoading = computed(() => props.accountsLoading)

const selectedId = ref<string | null>(null)
const approvedBankReference = ref('')
const localBankAccountError = ref('')
const localBankReferenceError = ref('')

// Track edits since the last server error so a stale field-level
// API error mutes the moment the operator starts retyping.
// (mute-on-edit pattern from `.claude/rules/ux-principles.md`)
const hasEditedSinceApiError = ref(false)
watch(
  () => props.apiError,
  () => {
    hasEditedSinceApiError.value = false
  },
)
const visibleApiError = computed(() =>
  props.apiError && !hasEditedSinceApiError.value ? props.apiError : '',
)

const options = computed(() =>
  (accounts.value ?? []).map((account: TreasuryBankAccount) => ({
    label: `${account.accountName} · ${account.bankName}`,
    value: account.id,
  })),
)

const selectedAccount = computed(
  () => (accounts.value ?? []).find((account) => account.id === selectedId.value) ?? null,
)

// Auto-select the default account when the dialog opens or when the
// accounts list resolves for the first time.
watch(
  [() => props.visible, accounts],
  ([isVisible, list]) => {
    if (!isVisible) return
    if (selectedId.value && list?.some((a) => a.id === selectedId.value)) return
    const def = list?.find((a) => a.isDefault) ?? list?.[0] ?? null
    selectedId.value = def?.id ?? null
  },
  { immediate: true },
)

// Reset local field state on close.
watch(
  () => props.visible,
  (isVisible) => {
    if (!isVisible) {
      approvedBankReference.value = ''
      localBankAccountError.value = ''
      localBankReferenceError.value = ''
    }
  },
)

const trimmedReference = computed(() => approvedBankReference.value.trim())
const expectedInvestorReference = computed(() => (props.investorReference ?? '').trim())

function handleAccountChange(value: string | null) {
  selectedId.value = value
  if (localBankAccountError.value) localBankAccountError.value = ''
  hasEditedSinceApiError.value = true
}

function handleReferenceInput(value: string) {
  approvedBankReference.value = value
  if (localBankReferenceError.value) localBankReferenceError.value = ''
  hasEditedSinceApiError.value = true
}

function handleConfirm() {
  let hasError = false
  if (!selectedId.value) {
    localBankAccountError.value = 'Pick the treasury account that received this transfer.'
    hasError = true
  }
  if (trimmedReference.value.length < MIN_BANK_REFERENCE_LENGTH) {
    localBankReferenceError.value = `Bank reference must be at least ${MIN_BANK_REFERENCE_LENGTH} characters.`
    hasError = true
  } else if (
    expectedInvestorReference.value !== '' &&
    trimmedReference.value.toUpperCase() !== expectedInvestorReference.value.toUpperCase()
  ) {
    localBankReferenceError.value = `Bank reference must match the investor's UTR (${expectedInvestorReference.value}).`
    hasError = true
  }
  if (hasError || !selectedId.value) return
  emit('confirm', {
    treasuryBankAccountId: selectedId.value,
    approvedBankReference: trimmedReference.value,
  })
}

function handleClose() {
  if (props.loading) return
  emit('update:visible', false)
}
</script>

<template>
  <Dialog
    :visible="visible"
    width="520px"
    max-height="80vh"
    :show-close-icon="!loading"
    :dismissable="!loading"
    @update:visible="(v) => !v && handleClose()"
  >
    <template #header>
      <Text type="page-title">Approve deposit request</Text>
    </template>

    <Box flex direction="col" gap="3">
      <Text type="body-sm" color="muted">
        Confirm the treasury account that received the investor's transfer and capture the bank's
        matched reference. Approving credits the investor's wallet immediately and writes the ledger
        row.
      </Text>

      <FormField label="Treasury bank account" required :error="localBankAccountError">
        <Select
          :model-value="selectedId"
          :options="options"
          :loading="accountsLoading"
          :disabled="loading || accountsLoading"
          placeholder="Choose a treasury account"
          filter
          @update:model-value="handleAccountChange"
        />
      </FormField>

      <FormField
        label="Bank reference"
        required
        :error="localBankReferenceError"
        :helper-text="
          expectedInvestorReference
            ? `Must match the investor's UTR: ${expectedInvestorReference}`
            : ''
        "
      >
        <Input
          :model-value="approvedBankReference"
          placeholder="BNK-MATCH-987"
          :disabled="loading"
          @update:model-value="handleReferenceInput"
        />
      </FormField>

      <Box v-if="selectedAccount" class="confirm-summary" padding="3">
        <Text type="body-sm">
          Crediting from
          <Text is="span" type="body-sm" weight="semibold">{{ selectedAccount.accountName }}</Text>
          at
          <Text is="span" type="body-sm" weight="semibold">{{ selectedAccount.bankName }}</Text>
          ({{ selectedAccount.currencyCode }}).
        </Text>
      </Box>

      <Message
        v-if="visibleApiError"
        color="danger"
        variant="outlined"
        size="small"
        :message="visibleApiError"
      />
    </Box>

    <template #footer>
      <Box flex justify="end" gap="2">
        <Button
          label="Cancel"
          variant="text"
          color="secondary"
          :disabled="loading"
          @click="handleClose"
        />
        <Button label="Approve" color="primary" :loading="loading" @click="handleConfirm" />
      </Box>
    </template>
  </Dialog>
</template>

<style scoped>
.confirm-summary {
  background: var(--p-primary-50);
  border: 1px solid var(--p-primary-200, var(--p-primary-100));
  border-radius: var(--p-border-radius-md);
}
</style>
